import { checkOrgStatus } from 'services/api';
import { User } from '../types/auth';
import { getUserData } from './encryption';

/**
 * Get the current user data
 */
const getUser = () => getUserData();
/**
 * Check if user is a subscribed user with active subscription
 */
export const isSubscribedUser = (): boolean => {
  const user = getUser();
  if (!user) return false;

  // ✅ Super admin has all access
  if (user.role === "super_admin") return true;

  return user.user_type === "subscribed" && user.subscription_status === "active";
};

export const isSecurityTrialExpired = (): boolean => {
  const user = getUser();
  const trialExpiry = user?.security_trial_expires;

  if (trialExpiry) {
    try {
      const expiryDate = new Date(trialExpiry);
      const nowUtc = new Date(new Date().toISOString()); // Ensure UTC comparison
      return expiryDate < nowUtc;
    } catch (error) {
      console.error('Invalid trial expiry date:', error);
      return true; // Assume expired on parsing error
    }
  }

  // If no expiry set, treat as expired
  return true;
};

/**
 * Check if user is a trial user with an active security trial
 */
export const isTrialUser = (): boolean => {
  const user = getUser();
  if (!user) return false;

  if (isSecurityTrialExpired()) return false;

  return user.user_type === "trial" && !!user.security_trial;
};

/**
 * Check if trial user is allowed to initiate a new scan
 * Returns false if trial is expired or credits are exhausted
*/
export const canTrialUserScan = async (): Promise<boolean> => {
  const user = getUser();
  if (!user) return false;

  // ✅ Super admin has all access
  if (user.role === "super_admin") return true;

  if (user.user_type !== "trial") return false;

  if (isSecurityTrialExpired()) return false;

  const orgTrial = await checkOrgStatus();
  if (orgTrial?.trial_status === "expired") return false;

  return !!user.security_trial && (!user.use_credit_system || (user.remaining_credits || 0) > 0);
};

/**
 * Check if trial user is allowed to solve a finding
 * Returns false if trial expired and finding is unsolved
*/
export const canTrialUserSolve = async (findingSolved: number | null): Promise<boolean> => {
  const user = getUser();
  if (!user) return false;

  // ✅ Super admin has all access
  if (user.role === "super_admin") return true;

  if (findingSolved !== 0) return true;

  if (user.user_type !== "trial") return false;

  if (isSecurityTrialExpired()) return false;

  const orgTrial = await checkOrgStatus();

  if (orgTrial?.trial_status !== "expired") return true;

  return findingSolved !== 0;
};


export interface TrialData {
  securityTrial: boolean;
  securityTrialExpires: string | null;
  useCreditSystem: boolean;
  remainingCredits: number;
  userRole: string;
}

/**
 * Extract trial data from user object
 */
export const extractTrialData = (user: User | null): TrialData => {
  if (!user) {
    return {
      securityTrial: false,
      securityTrialExpires: null,
      useCreditSystem: false,
      remainingCredits: 0,
      userRole: 'user'
    };
  }

  return {
    securityTrial: user.security_trial || false,
    securityTrialExpires: user.security_trial_expires || null,
    useCreditSystem: user.use_credit_system || false,
    remainingCredits: user.remaining_credits || 0,
    userRole: user.role || 'user'
  };
};

/**
 * Create trial headers for API requests
 */
export const createTrialHeaders = (user: User | null): Record<string, string> => {
  if (!user) {
    return {};
  }

  const trialData = extractTrialData(user);
  
  return {
    'x-security-trial': trialData.securityTrial.toString(),
    'x-security-trial-expires': trialData.securityTrialExpires || '',
    'x-use-credit-system': trialData.useCreditSystem.toString(),
    'x-remaining-credits': trialData.remainingCredits.toString(),
    'x-user-role': trialData.userRole
  };
};

/**
 * Check if user has valid security trial
 * Now uses JWT data format from backend
 */
export const hasValidSecurityTrial = (user: User | null): boolean => {
  if (!user) return false;

  // Super admin always has access
  if (user.role === 'super_admin') return true;

  // Check if security trial is active
  if (!user.security_trial) return false;

  // Check if trial has expired
  if (user.security_trial_expires) {
    const expiryDate = new Date(user.security_trial_expires);
    const now = new Date();
    if (expiryDate <= now) return false;
  }

  return true;
};

/**
 * Get trial status message for UI
 */
export const getTrialStatusMessage = (user: User | null): string | null => {
  if (!user) return null;

  if (user.role === 'super_admin') return null; // No message for super admin

  if (!user.security_trial) {
    return 'Security trial is not active for your account. Please contact support to enable it.';
  }

  if (user.security_trial_expires) {
    const expiryDate = new Date(user.security_trial_expires);
    const now = new Date();
    if (expiryDate <= now) {
      return 'Your security trial period has expired. Please upgrade to continue using the service.';
    }
  }

  if (user.use_credit_system && (user.remaining_credits || 0) <= 0) {
    return 'You have exhausted your trial credits. Please upgrade to continue using the service.';
  }

  return null;
};
